import React, { useState, useEffect, memo, useCallback, useRef } from 'react';
import { TextField, alpha, Typography, Box, useTheme } from '@mui/material';
import debounce from 'lodash/debounce';

const NotesField = memo(({ initialValue = '', onChange, isEditing, placeholder = "Enter important notes about this hotel", maxLength = 500 }) => {
  const [value, setValue] = useState(initialValue);
  const [rows, setRows] = useState(3);
  const theme = useTheme();
  
  // Create a more efficient debounced function with longer delay
  const debouncedFn = useRef(
    debounce((value) => {
      onChange(value);
    }, 300)  // Increased from 150ms to 300ms for better performance
  ).current;

  // Update local state when initialValue changes
  useEffect(() => {
    setValue(initialValue);
    
    // Calculate rows based on initial value
    const lineCount = (initialValue.match(/\n/g) || []).length + 1;
    setRows(Math.min(Math.max(3, lineCount), 8)); // Reduced max rows from 12 to 8
  }, [initialValue]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  // Memoize the change handler to prevent recreating on each render
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      setValue(newValue); // Update local state immediately for responsive UI
      
      // Calculate rows immediately for better responsiveness
      const lineCount = (newValue.match(/\n/g) || []).length + 1;
      setRows(Math.min(Math.max(3, lineCount), 8)); // Reduced max rows from 12 to 8
      
      // Debounce only the parent update
      debouncedFn(newValue);
    }
  }, [maxLength, debouncedFn]);

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        multiline
        rows={rows}
        size="small"
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        variant="outlined"
        inputProps={{
          maxLength: maxLength
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'transparent',
            fontSize: '0.8rem',
            fontFamily: 'StrawberrySansText',
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.primary.main, 0.3),
            borderRadius: 1,
            transition: 'all 0.15s ease-in-out',
            '& fieldset': {
              border: 'none'
            },
            '& textarea': {
              padding: '8px 10px',
              lineHeight: 1.5,
              letterSpacing: '0.01em'
            },
            '&:hover': {
              borderColor: theme => alpha(theme.palette.primary.main, 0.5),
              backgroundColor: theme => alpha(theme.palette.background.paper, 0.8)
            },
            '&.Mui-focused': {
              borderColor: theme => alpha(theme.palette.primary.main, 0.7),
              backgroundColor: theme => alpha(theme.palette.background.paper, 0.9),
              boxShadow: theme => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
            }
          }
        }}
      />
      {isEditing && (
        <>
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 4,
              right: 8,
              color: theme => {
                const percentFilled = (value.length / maxLength) * 100;
                return percentFilled > 90 
                  ? alpha(theme.palette.error.main, 0.8)
                  : percentFilled > 75
                    ? alpha(theme.palette.warning.main, 0.8)
                    : alpha(theme.palette.text.secondary, 0.7);
              },
              fontSize: '0.65rem',
              fontWeight: value.length > (maxLength * 0.9) ? 600 : 400,
              transition: 'all 0.2s ease'
            }}
          >
            {value.length}/{maxLength}
          </Typography>
          {/* Subtle gradient at the top */}
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              height: '6px', 
              background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.05)}, transparent)`,
              pointerEvents: 'none',
              borderTopLeftRadius: 1,
              borderTopRightRadius: 1
            }} 
          />
        </>
      )}
    </Box>
  );
});

NotesField.displayName = 'NotesField';

export default NotesField; 